/**
 * SessionExpiryBanner — keeps the user on top of their Stanford SSO ID
 * token's expiry without nagging them while they're actively working.
 *
 * Behavior:
 *   - When the token is within 10 min of expiry AND the user has
 *     interacted (mousemove/keydown/click) within the last minute, fire
 *     a silent Google One Tap re-auth in the background. If it
 *     succeeds, a fresh token replaces the old one and the banner
 *     simply doesn't render.
 *   - When the token is within 5 min of expiry, show the banner — but
 *     with a "Refresh now" button that retries the silent re-auth on
 *     demand. "Sign in again" stays as the manual escape hatch.
 *   - When the token has expired, show the urgent variant; only
 *     "Sign in again" works at that point.
 *
 * The token is a Google ID JWT; we decode the `exp` claim purely for
 * UX. The server already verifies on every API call.
 */
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

import { useAuth } from '@/auth/AuthContext.js';

const TOKEN_KEY = 'sumcp_google_id_token';
const WARN_THRESHOLD_MS = 5 * 60 * 1000;
const REFRESH_THRESHOLD_MS = 10 * 60 * 1000;
const ACTIVE_WINDOW_MS = 60 * 1000;

function decodeExp(jwt: string): number | null {
  const parts = jwt.split('.');
  if (parts.length < 2) return null;
  try {
    const payload = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const json = JSON.parse(atob(padded)) as { exp?: number };
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function SessionExpiryBanner(): JSX.Element | null {
  const { token, signOut, refreshSession } = useAuth();
  const [now, setNow] = useState(() => Date.now());
  const lastActivityRef = useRef<number>(Date.now());
  const refreshAttemptedRef = useRef<number>(0);
  const [refreshing, setRefreshing] = useState(false);

  // Tick clock every 5s so the countdown updates smoothly. We also
  // recheck active-refresh conditions on each tick.
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 5_000);
    return () => window.clearInterval(t);
  }, []);

  // Activity tracking. Throttled to one update per 5s so we're not
  // re-rendering on every mousemove.
  useEffect(() => {
    let lastWrite = 0;
    const onActivity = (): void => {
      const t = Date.now();
      if (t - lastWrite < 5_000) return;
      lastWrite = t;
      lastActivityRef.current = t;
    };
    window.addEventListener('mousemove', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity, { passive: true });
    window.addEventListener('click', onActivity, { passive: true });
    window.addEventListener('scroll', onActivity, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('click', onActivity);
      window.removeEventListener('scroll', onActivity);
    };
  }, []);

  const tryRefresh = useCallback(
    async (reason: 'auto' | 'manual'): Promise<void> => {
      if (refreshing) return;
      setRefreshing(true);
      refreshAttemptedRef.current = Date.now();
      try {
        await refreshSession();
      } finally {
        setRefreshing(false);
        // For auto-refresh, if it failed (token didn't change), wait at
        // least 60s before retrying. Manual refreshes can retry sooner.
        if (reason === 'auto') {
          // refreshAttemptedRef stays set; next eligibility check honors it.
        }
      }
    },
    [refreshSession, refreshing],
  );

  // Auto-refresh trigger — fires once per minute when the token is
  // within REFRESH_THRESHOLD and the user has interacted recently.
  useEffect(() => {
    if (!token) return;
    const exp = decodeExp(token);
    if (!exp) return;
    const remaining = exp - now;
    if (remaining > REFRESH_THRESHOLD_MS) return;
    if (remaining <= 0) return; // already expired — only manual retry helps
    const userActive = now - lastActivityRef.current < ACTIVE_WINDOW_MS;
    if (!userActive) return;
    const sinceLastAttempt = now - refreshAttemptedRef.current;
    if (sinceLastAttempt < ACTIVE_WINDOW_MS) return;
    void tryRefresh('auto');
  }, [token, now, tryRefresh]);

  if (!token) return null;
  const exp = decodeExp(token);
  if (!exp) return null;
  const remaining = exp - now;
  if (remaining > WARN_THRESHOLD_MS) return null;

  const expired = remaining <= 0;
  const minutes = Math.max(0, Math.floor(remaining / 60_000));
  const seconds = Math.max(0, Math.floor((remaining % 60_000) / 1000));

  return (
    <div
      style={{
        background: expired ? '#fff1f0' : '#fff8e6',
        border: `1px solid ${expired ? '#e8b3b3' : '#f0d9a0'}`,
        color: expired ? '#7a1f1f' : '#7a5a10',
        padding: '8px 14px',
        fontSize: 13,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        justifyContent: 'space-between',
      }}
      role="alert"
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <AlertTriangle size={14} />
        {expired ? (
          <>Your session has expired. Sign in again to keep working.</>
        ) : (
          <>
            Your session expires in{' '}
            <strong>
              {minutes}m {seconds}s
            </strong>
            . Click <strong>Refresh</strong> to extend without losing in-progress work.
          </>
        )}
      </span>
      <span style={{ display: 'inline-flex', gap: 6 }}>
        {!expired && (
          <button
            type="button"
            onClick={() => void tryRefresh('manual')}
            disabled={refreshing}
            style={{
              background: 'transparent',
              border: '1px solid currentColor',
              borderRadius: 6,
              padding: '4px 10px',
              cursor: refreshing ? 'wait' : 'pointer',
              color: 'inherit',
              fontSize: 13,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
            title="Re-authenticate silently via Google. If it doesn't work, click Sign in again."
          >
            <RefreshCw size={13} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            localStorage.removeItem(TOKEN_KEY);
            document.cookie = 'sumcp_session=; Path=/; Max-Age=0';
            signOut();
            window.location.reload();
          }}
          style={{
            background: 'transparent',
            border: '1px solid currentColor',
            borderRadius: 6,
            padding: '4px 10px',
            cursor: 'pointer',
            color: 'inherit',
            fontSize: 13,
          }}
        >
          Sign in again
        </button>
      </span>
    </div>
  );
}
